import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaCommentAlt, FaRobot, FaBolt } from 'react-icons/fa';

import './Home.scss';
import HeroSlide from '../components/hero-slide/HeroSlide';
import MovieList from '../components/movie-list/MovieList';
import ChatBox from "../components/ChatBox";
import LoginModal from "./account/LoginModal";
import RegisterModal from "./account/RegisterModal";
import GenreList from '../components/genre-list/GenreList';
import CountryList from '../components/genre-list/CountryList';
import RankedMovieList from '../components/movie-list/RankedMovieList';
import Top10SlantList from '../components/movie-list/Top10SlantList';
import TrendingTop10List from '../components/movie-list/TrendingTop10List';
import GenreMovieList from '../components/movie-list/GenreMovieList';
import LandscapeMovieList from '../components/movie-list/LandscapeMovieList';
import SpotlightList from '../components/movie-list/SpotlightList';
import TrailerCardList from '../components/movie-list/TrailerCardList';
import MagazineTrailerSection from '../components/movie-list/MagazineTrailerSection';
import NeonMovieList from '../components/movie-list/NeonMovieList';
import TrailerOnlyList from '../components/movie-list/TrailerOnlyList';
import TopRankedSingleList from '../components/movie-list/TopRankedSingleList';

const MOVIE_LIST_TYPE = {
    new:         'similar',
    popular:     'popular',
    toprated:    'top-rated',
    newupdate:   'new-update',
    hotmonth:    'hotMonth',
};

const seeAllLink = (section) => {
    if (section.type === 'genres')  return '/genre';
    if (section.type === 'country') return `/search/country/${encodeURIComponent(section.param || '')}`;
    if (section.type === 'neon' || section.type === 'trailer-cards' || section.type === 'magazine-trailer' || section.type === 'trailer-only') return '/trailer';
    return '/catalog';
};

const SectionHeader = ({ title, to }) => (
    <div className="section__header mb-2">
        <h2 className="section__title">{title}</h2>
        <Link to={to} className="section__see-all">
            <span className="section__see-all__text">Xem tất cả</span>
            <span className="section__see-all__arrow">›</span>
        </Link>
    </div>
);


const Home = ({ refreshKey }) => {
    const [showChatBox, setShowChatBox] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [userData] = useState(() => {
        const saved = localStorage.getItem('USER_LOGIN');
        return saved ? JSON.parse(saved) : null;
    });
    const [sections, setSections] = useState([]);
    const [isVip, setIsVip] = useState(false);
    const [cardStyle, setCardStyle] = useState('poster');
    const [newUpdateBadge, setNewUpdateBadge] = useState('NEW');
    const pageRef = useRef({});

    useEffect(() => {
        fetch('/movies-home-sections-api.php')
            .then(r => r.json())
            .then(d => { if (d.success) setSections(d.data); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetch('/movies-settings-api.php')
            .then(r => r.json())
            .then(d => {
                if (!d.success) return;
                const map = {};
                d.data.forEach(row => { map[row.setting_key] = row.setting_value; });
                if (map['ui_movie_card_style']) setCardStyle(map['ui_movie_card_style']);
                if (map['new_update_badge']) setNewUpdateBadge(map['new_update_badge']);
                if (map['ui_accent_color']) {
                    document.documentElement.style.setProperty('--ui-accent', map['ui_accent_color']);
                }
                if (map['ui_text_color']) {
                    document.documentElement.style.setProperty('--ui-text-color', map['ui_text_color']);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!userData?.id) return;
        fetch(`/movies-vip-admin-api.php?userId=${userData.id}`)
            .then(r => r.json())
            .then(d => {
                if (d.success && d.vipExpiry && new Date(d.vipExpiry) > new Date()) {
                    setIsVip(true);
                }
            })
            .catch(() => {});
    }, [userData]);

    const toggleChatBox = () => {
        if (!userData) {
            Swal.fire({
                title: 'Thông báo',
                text: 'Vui lòng đăng nhập để dùng trợ lý phim AI!',
                icon: 'warning',
                confirmButtonText: 'Đăng nhập',
                showCancelButton: true,
                cancelButtonText: 'Để sau',
                reverseButtons: true,
            }).then(result => { if (result.isConfirmed) setShowLoginModal(true); });
            return;
        }
        setShowChatBox(v => !v);
    };

    const renderMovieSection = (section) => {
        const listType = MOVIE_LIST_TYPE[section.type] ?? 'similar';
        const p = pageRef.current[listType] ?? 0;
        pageRef.current[listType] = p + 1;
        return (
            <MovieList
                type={listType}
                page={p}
                cardStyle={cardStyle}
                refreshKey={refreshKey}
            />
        );
    };

    // Reset page counter per type mỗi render
    pageRef.current = {};

    // Group consecutive country sections into one shared frame
    const rendered = [];
    let i = 0;
    while (i < sections.length) {
        const s = sections[i];
        if (s.type === 'country' || s.type === 'genre-ranked') {
            const group = [];
            const typeKey = s.type;
            while (i < sections.length && sections[i].type === typeKey) {
                group.push(sections[i]);
                i++;
            }
            rendered.push(
                <div key={`cg-${group[0].id}`} className="country-sections">
                    {group.map((cs) => (
                        <div key={cs.id} className="country-block">
                            <div className="country-block__header">
                                <h2 className="country-block__title">{cs.title}</h2>
                                <Link to={seeAllLink(cs)} className="country-block__link">
                                    Xem tất cả <span>›</span>
                                </Link>
                            </div>
                            {cs.type === 'country'
                                ? <RankedMovieList country={cs.param} />
                                : <RankedMovieList genreId={cs.param} />
                            }
                        </div>
                    ))}
                </div>
            );
        } else if (s.type === 'banner') {
            if (!isVip) {
                let bannerImg = '', bannerLink = '#';
                try { const p = JSON.parse(s.param || '{}'); bannerImg = p.img || ''; bannerLink = p.link || '#'; } catch {}
                if (bannerImg) {
                    rendered.push(
                        <div key={s.id} className="section mb-3">
                            <a href={bannerLink} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'block', borderRadius: 10, overflow: 'hidden', lineHeight: 0 }}>
                                <img src={bannerImg} alt="Quảng cáo"
                                    style={{ width: '100%', height: 'auto', maxHeight: 120, objectFit: 'cover', display: 'block', borderRadius: 10 }} />
                            </a>
                        </div>
                    );
                }
            }
            i++;
        } else {
            rendered.push(
                <div key={s.id} className="section mb-3">
                    <SectionHeader title={s.title} to={seeAllLink(s)} />
                    {s.type === 'genres'
                        ? <GenreList />
                        : s.type === 'countries'
                            ? <CountryList />
                            : s.type === 'genre'
                                ? <GenreMovieList genreId={s.param} />
                                : s.type === 'landscape'
                                    ? <LandscapeMovieList genreId={s.param} />
                                    : s.type === 'landscape-new'
                                        ? <LandscapeMovieList type="new" />
                                        : s.type === 'landscape-pop'
                                            ? <LandscapeMovieList type="popular" />
                                            : s.type === 'spotlight'
                                                ? <SpotlightList genreId={s.param} />
                                                : s.type === 'trailer-cards'
                                                    ? <TrailerCardList genreId={s.param || null} />
                                                    : s.type === 'magazine-trailer'
                                                        ? <MagazineTrailerSection genreId={s.param || null} />
                                                        : s.type === 'neon'
                                                            ? <NeonMovieList genreId={s.param || null} />
                                                            : s.type === 'trailer-only'
                                                                ? <TrailerOnlyList genreId={s.param || null} />
                                                                : s.type === 'top-ranked-single'
                                                                    ? <TopRankedSingleList />
                                                                    : s.type === 'top-single-slant'
                                                                        ? <Top10SlantList />
                                                                        : renderMovieSection(s)
                    }
                </div>
            );
            i++;
        }
    }

    return (
        <>
            <HeroSlide />
            <div className="container home-container" style={{ position: 'relative', zIndex: 5 }}>

                {/* ── Section: Xem nhiều trong tuần (TRENDING) — đặt TRÊN Mới cập nhật ── */}
                <div className="section mb-3">
                    <div className="section__header mb-2">
                        <span className="home-new-update-badge">
                            <FaBolt size={10} /> Xem nhiều trong tuần
                        </span>
                        <Link to="/catalog" className="section__see-all">
                            <span className="section__see-all__text">Xem tất cả</span>
                            <span className="section__see-all__arrow">›</span>
                        </Link>
                    </div>
                    <TrendingTop10List type="trending" size={10} />
                </div>

                {/* ── Section: Phim mới cập nhật (NEW UPDATE) ── */}
                <div className="section mb-3">
                    <div className="section__header mb-2">
                        <span className="home-new-update-badge">
                            <FaBolt size={10} /> Mới cập nhật
                        </span>
                        <Link to="/catalog" className="section__see-all">
                            <span className="section__see-all__text">Xem tất cả</span>
                            <span className="section__see-all__arrow">›</span>
                        </Link>
                    </div>
                    <MovieList
                        type="new-update"
                        page={0}
                        cardStyle={cardStyle}
                        newUpdateBadge={newUpdateBadge}
                        refreshKey={refreshKey}
                    />
                </div>

                {rendered}

                {!showChatBox && (
                    <div
                        className="floating-chat-button"
                        onClick={toggleChatBox}
                        title="Trợ lý phim AI"
                        style={{
                            position: 'fixed', bottom: '20px', right: '20px',
                            width: '60px', height: '60px',
                            backgroundColor: '#3b82f6', borderRadius: '50%',
                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                            cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            zIndex: 1000, transition: 'all 0.3s ease',
                        }}
                    >
                        <FaCommentAlt style={{ color: 'white', fontSize: '24px' }} />
                        {userData && (
                            <div style={{
                                position: 'absolute', top: '-5px', right: '-5px',
                                backgroundColor: '#f59e0b', borderRadius: '50%',
                                width: '20px', height: '20px',
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                            }}>
                                <FaRobot style={{ color: '#fff', fontSize: '12px' }} />
                            </div>
                        )}
                    </div>
                )}

                <ChatBox showChatBox={showChatBox} toggleChatBox={toggleChatBox} />

                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                    onSwitchToRegister={() => { setShowLoginModal(false); setShowRegisterModal(true); }}
                />
                <RegisterModal
                    isOpen={showRegisterModal}
                    onClose={() => setShowRegisterModal(false)}
                    onSwitchToLogin={() => { setShowRegisterModal(false); setShowLoginModal(true); }}
                />
            </div>
        </>
    );
};

Home.propTypes = {
    refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default Home;
